/**
 * TTS Settings Store (Zustand)
 * Local-only preferences for text-to-speech.
 * Device-specific — not synced to backend.
 * Backed by the centralized localPreferences service.
 *
 * Per-course voice settings (provider, voiceName) are in CoursePreference.
 * This store only manages autoPlay (global) and localPreferences loading.
 */

import { create } from 'zustand';
import {
  getLocalPreferences,
  isLocalPreferencesLoaded,
  loadLocalPreferences,
  updateLocalPreferences,
} from '../services/localPreferences';

interface TTSSettingsState {
  autoPlay: boolean;
  loaded: boolean;

  load: () => Promise<void>;
  setAutoPlay: (enabled: boolean) => Promise<void>;
}

export const useTTSSettingsStore = create<TTSSettingsState>((set) => ({
  autoPlay: true,
  loaded: false,

  load: async () => {
    try {
      const prefs = isLocalPreferencesLoaded()
        ? getLocalPreferences()
        : await loadLocalPreferences();
      set({
        autoPlay: prefs.ttsAutoPlay,
        loaded: true,
      });
    } catch {
      set({ loaded: true });
    }
  },

  setAutoPlay: async (enabled: boolean) => {
    set({ autoPlay: enabled });
    try {
      await updateLocalPreferences({ ttsAutoPlay: enabled });
    } catch {
      // Best-effort persistence
    }
  },
}));
